import React, { useEffect, useMemo, useState } from 'react'
import { CButton, CFormCheck, CFormInput, CDateRangePicker } from '@coreui/react-pro'
import moment from 'moment'

const MOBILE_BREAKPOINT = 768
const DATE_INPUT_FORMATS = ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', 'DD/MM/YYYY', 'D/M/YYYY', moment.ISO_8601]

const FilterByPopover = ({
  buttonLabel = 'Filter by',
  columnFilter = {},
  setColumnFilter,
  filterSections = [],
}) => {
  const [open, setOpen] = useState(false)
  const [draftFilter, setDraftFilter] = useState(columnFilter || {})
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false,
  )

  // watch viewport
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // sync draft from applied filter when popover opens
  useEffect(() => {
    if (open) setDraftFilter(columnFilter || {})
  }, [open, columnFilter])

  // lock body scroll on mobile bottom-sheet
  useEffect(() => {
    if (open && isMobile) {
      const original = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = original
      }
    }
  }, [open, isMobile])

  const activeFilterCount = useMemo(() => {
    return filterSections.reduce((acc, section) => {
      if (section.type === 'date-range') {
        const hasStart = !!columnFilter?.[section.startKey]?.value
        const hasEnd = !!columnFilter?.[section.endKey]?.value
        return acc + (hasStart || hasEnd ? 1 : 0)
      }

      const val = columnFilter?.[section.key]?.value
      return acc + (val ? 1 : 0)
    }, 0)
  }, [columnFilter, filterSections])

  const onMultiToggle = (key, value) => {
    const current = (draftFilter?.[key]?.value || '').split(',').filter(Boolean)
    const next = current.includes(value)
      ? current.filter((x) => x !== value)
      : [...current, value]

    setDraftFilter((prev) => ({
      ...prev,
      [key]: next.length ? { value: next.join(',') } : null,
    }))
  }

  const onSingleChange = (key, value) => {
    setDraftFilter((prev) => ({
      ...prev,
      [key]: value ? { value } : null,
    }))
  }

  const normalizeDate = (value) => {
    if (!value) return ''

    // Date object (from calendar clicks) or a complete typed string
    const parsed = moment(value, DATE_INPUT_FORMATS, true)
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
  }

  const parseInputDate = (value) => {
    if (!value) return null

    const parsed = moment(value, DATE_INPUT_FORMATS, true)
    return parsed.isValid() ? parsed.toDate() : null
  }

  const onDateRangeChange = (startKey, endKey, startValue, endValue) => {
    const start = normalizeDate(startValue)
    const end = normalizeDate(endValue)

    setDraftFilter((prev) => ({
      ...prev,
      [startKey]: start ? { value: start } : null,
      [endKey]: end ? { value: end } : null,
    }))
  }

  const resetAll = () => {
    const nextDraft = { ...(draftFilter || {}) }
    filterSections.forEach((s) => {
      if (s.type === 'date-range') {
        nextDraft[s.startKey] = null
        nextDraft[s.endKey] = null
      } else {
        nextDraft[s.key] = null
      }
    })

    setDraftFilter(nextDraft)
    setColumnFilter(nextDraft)
  }

  const applyFilter = () => {
    setColumnFilter(draftFilter)
    setOpen(false)
  }

  const renderSections = () =>
    filterSections.map((section) => {
      if (section.type === 'multi-checkbox') {
        const selected = (draftFilter?.[section.key]?.value || '').split(',').filter(Boolean)
        return (
          <div className="mb-3" key={section.key}>
            <div className="fw-semibold mb-2">{section.label}</div>
            <div style={{ maxHeight: '170px', overflowY: 'auto' }}>
              {(section.options || []).map((opt) => (
                <CFormCheck
                  key={opt.value}
                  id={`${section.key}-${opt.value}`}
                  label={opt.label}
                  checked={selected.includes(opt.value)}
                  onChange={() => onMultiToggle(section.key, opt.value)}
                  className="mb-2"
                />
              ))}
            </div>
          </div>
        )
      }

      if (section.type === 'text') {
        const sectionValue = draftFilter?.[section.key]?.value || ''
        return (
          <div className="mb-3" key={section.key}>
            <div className="fw-semibold mb-2">{section.label}</div>
            <CFormInput
              placeholder={section.placeholder || 'Type...'}
              value={sectionValue}
              onChange={(e) => onSingleChange(section.key, e.target.value)}
            />
          </div>
        )
      }

      if (section.type === 'date-range') {
        const startDate = draftFilter?.[section.startKey]?.value || ''
        const endDate = draftFilter?.[section.endKey]?.value || ''

        return (
          <div className="mb-3" key={`${section.startKey}-${section.endKey}`}>
            <div className="fw-semibold mb-2">{section.label}</div>
            <CDateRangePicker
              size="sm"
              calendars={1}
              inputDateParse={parseInputDate}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={(value) =>
                onDateRangeChange(section.startKey, section.endKey, value, endDate)
              }
              onEndDateChange={(value) =>
                onDateRangeChange(section.startKey, section.endKey, startDate, value)
              }
            />
          </div>
        )
      }

      return null
    })

  return (
    <div className="position-relative">
      <CButton
        color="primary"
        variant="outline"
        onClick={() => setOpen((v) => !v)}
        style={{ whiteSpace: 'nowrap' }}
      >
        {buttonLabel}
        {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
      </CButton>

      {/* Desktop popover */}
      {open && !isMobile && (
        <div
          className="px-3 py-2"
          style={{
            position: 'absolute',
            top: '44px',
            right: 0,
            width: '360px',
            maxWidth: '92vw',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
            zIndex: 1050,
          }}
        >
          <div className="d-flex justify-content-end align-items-center mb-2">
            <button
              type="button"
              aria-label="Close filter"
              onClick={() => setOpen(false)}
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '30px',
                fontWeight: 500,
                lineHeight: 1,
                width: '40px',
                height: '40px',
                display: 'inline-flex',
                alignItems: 'start',
                justifyContent: 'end',
                cursor: 'pointer',
                color: '#4b5563',
                padding: 0,
              }}
            >
              ×
            </button>
          </div>

          <div style={{ paddingRight: '2px' }}>
            {renderSections()}
          </div>

          <div className="d-flex justify-content-end flex-wrap gap-2 mt-2 mb-1">
            <CButton color="primary" onClick={applyFilter}>
              Apply
            </CButton>
            <CButton color="light" onClick={resetAll}>
              Reset All
            </CButton>
          </div>
        </div>
      )}

      {/* Mobile bottom sheet */}
      {open && isMobile && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.35)',
              zIndex: 1040,
            }}
          />
          <div
            className="px-3 py-2"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              background: '#fff',
              borderTopLeftRadius: '14px',
              borderTopRightRadius: '14px',
              boxShadow: '0 -8px 24px rgba(0,0,0,0.18)',
              zIndex: 1050,
              maxHeight: '82vh',
              overflow: 'visible',
            }}
          >
            <div
              style={{
                width: '42px',
                height: '4px',
                borderRadius: '999px',
                background: '#d1d5db',
                margin: '6px auto 10px',
              }}
            />
            <div className="d-flex justify-content-end align-items-center mb-2">
              <button
                type="button"
                aria-label="Close filter"
                onClick={() => setOpen(false)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: '30px',
                  fontWeight: 500,
                  lineHeight: 1,
                  width: '40px',
                  height: '40px',
                  display: 'inline-flex',
                  alignItems: 'start',
                  justifyContent: 'end',
                  cursor: 'pointer',
                  color: '#4b5563',
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>

            <div>
              {renderSections()}
            </div>

            <div className="d-flex gap-2 mt-2 mb-2">
              <CButton color="primary" onClick={applyFilter} style={{ flex: 1 }}>
                Apply
              </CButton>
              <CButton color="light" onClick={resetAll} style={{ flex: 1 }}>
                Reset All
              </CButton>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default FilterByPopover